import { useId } from 'react';

import {
  Field,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import type { PermissionSetOption } from '../permission-set-options';

/*
 * The permission-set chooser (F02 R24), shared by the add-user and user-editor
 * dialogs. A SINGLE-select group: at most one non-baseline set alongside the
 * baseline, or "none" (the baseline alone) — AC-463fbe56 / AC-de110ec1. Choosing
 * a set REPLACES the one held rather than adding (AC-ef201ead), which is exactly
 * single-select semantics: the chosen `value` is one set id (or NO_SET_VALUE),
 * never an accumulating list.
 *
 * It renders NATIVE radio inputs (not the base-ui RadioGroup the role chooser
 * uses): the chooser's selected state is read by the current state against the
 * account in force and asserted at the input level, so each option is a real
 * `<input type="radio">` carrying its own `checked`. The group is a labelled
 * `<fieldset>` ("Permission set") so it is a single named group landmark; each
 * option's native input is tied to its visible label by id for its accessible
 * name. The server (Identity, through the domain gate) is the authority on
 * assignment; this is the courtesy half (offer at-most-one, default none).
 */
export function PermissionSetChooser({
  options,
  value,
  onValueChange,
  name,
}: {
  /** The selectable sets (incl. the "none" option), in display order. */
  options: PermissionSetOption[];
  /** The currently-selected set id, or the NO_SET_VALUE "none" sentinel. */
  value: string;
  /** Called with the newly-chosen set id (or NO_SET_VALUE). */
  onValueChange: (next: string) => void;
  /**
   * The radio group's name, so the native inputs form ONE mutually-exclusive
   * group (and two choosers on one page never collide).
   */
  name: string;
}): React.ReactElement {
  const groupId = useId();
  return (
    <FieldSet>
      <FieldLegend variant="label">Permission set</FieldLegend>
      {/* A labelled fieldset is the single named "group" (axe region / the
          group the dialogs query by name). The options inside are native radios
          so the one chosen set carries a real `checked`. */}
      {options.map((option) => {
        const inputId = `${groupId}-${option.value}`;
        return (
          <Field key={option.value} orientation="horizontal">
            <input
              type="radio"
              id={inputId}
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onValueChange(option.value)}
              aria-label={option.label}
              className="size-4"
            />
            <FieldLabel htmlFor={inputId}>{option.label}</FieldLabel>
          </Field>
        );
      })}
    </FieldSet>
  );
}
